import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Globe, Bug, AlertTriangle, CheckCircle, Loader2, Play,
  Clock, Link as LinkIcon, Image, FileText, Smartphone,
  RefreshCw, Wrench, ShieldAlert, Search, BarChart3,
  ChevronDown, ChevronRight, Code, Copy, ExternalLink,
  Activity, Eye, Megaphone, Users, Shield, Zap
} from 'lucide-react';

// ── Severity config ──────────────────────────────────────────────────────────
const SEV = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/40', badge: 'bg-red-900/40 text-red-300 border-red-700', dot: 'bg-red-500', score: 25 },
  high:     { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/40', badge: 'bg-orange-900/40 text-orange-300 border-orange-700', dot: 'bg-orange-500', score: 15 },
  medium:   { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/40', badge: 'bg-amber-900/40 text-amber-300 border-amber-700', dot: 'bg-amber-500', score: 7 },
  low:      { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/40', badge: 'bg-blue-900/40 text-blue-300 border-blue-700', dot: 'bg-blue-400', score: 3 },
  info:     { bg: 'bg-zinc-500/15', text: 'text-zinc-400', border: 'border-zinc-500/40', badge: 'bg-zinc-900/40 text-zinc-300 border-zinc-700', dot: 'bg-zinc-500', score: 0 },
};

// ── Category config ───────────────────────────────────────────────────────────
const CAT = {
  availability: { icon: ShieldAlert, label: 'Availability', color: 'text-red-400' },
  performance:  { icon: Zap, label: 'Performance', color: 'text-yellow-400' },
  seo:          { icon: Search, label: 'SEO', color: 'text-green-400' },
  mobile:       { icon: Smartphone, label: 'Mobile', color: 'text-blue-400' },
  accessibility:{ icon: Eye, label: 'Accessibility', color: 'text-purple-400' },
  social:       { icon: Users, label: 'Social', color: 'text-pink-400' },
  analytics:    { icon: Activity, label: 'Analytics', color: 'text-cyan-400' },
  ux:           { icon: Globe, label: 'UX', color: 'text-indigo-400' },
  conversion:   { icon: Megaphone, label: 'Conversion', color: 'text-emerald-400' },
  trust:        { icon: Shield, label: 'Trust', color: 'text-amber-400' },
  security:     { icon: ShieldAlert, label: 'Security', color: 'text-red-500' },
  content:      { icon: FileText, label: 'Content', color: 'text-slate-400' },
};

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];
const CAT_ORDER = ['availability', 'security', 'performance', 'seo', 'mobile', 'accessibility', 'conversion', 'trust', 'ux', 'social', 'analytics', 'content'];

// ── Score ring ────────────────────────────────────────────────────────────────
const ScoreRing = ({ score }) => {
  if (score == null) return (
    <div className="w-28 h-28 rounded-full border-4 border-border flex items-center justify-center">
      <Globe className="w-8 h-8 text-muted-foreground" />
    </div>
  );
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : score >= 40 ? '#f97316' : '#ef4444';
  const label = score >= 80 ? 'Good' : score >= 60 ? 'Fair' : score >= 40 ? 'Poor' : 'Critical';
  const r = 44;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="relative w-28 h-28">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeWidth="8" className="text-border" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color }}>{score}</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
};

// ── Scan checklist tile ───────────────────────────────────────────────────────
const CheckTile = ({ label, ok, warn }) => (
  <div className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border ${
    ok   ? 'bg-emerald-900/20 border-emerald-700/40 text-emerald-300' :
    warn ? 'bg-amber-900/20 border-amber-700/40 text-amber-300' :
           'bg-red-900/20 border-red-700/40 text-red-300'
  }`}>
    {ok ? <CheckCircle className="w-3 h-3" /> : warn ? <AlertTriangle className="w-3 h-3" /> : <Bug className="w-3 h-3" />}
    {label}
  </div>
);

// ── Fix snippet viewer ────────────────────────────────────────────────────────
const FixSnippet = ({ snippet }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mt-2 rounded-md bg-black/50 border border-border overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-black/30">
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Code className="w-3 h-3" /> Suggested fix</span>
        <button onClick={copy} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
          <Copy className="w-3 h-3" />{copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="text-xs text-emerald-300 p-3 overflow-x-auto whitespace-pre-wrap leading-relaxed">{snippet}</pre>
    </div>
  );
};

// ── Issue card ────────────────────────────────────────────────────────────────
const IssueCard = ({ issue, onFix, showFix }) => {
  const [expanded, setExpanded] = useState(false);
  const s = SEV[issue.severity] || SEV.info;
  return (
    <div className={`rounded-lg border ${s.bg} ${s.border} overflow-hidden`}>
      <button
        className="w-full text-left p-3 flex items-start gap-3"
        onClick={() => setExpanded(e => !e)}
      >
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${s.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${s.badge}`}>{issue.severity}</span>
            {issue.auto_fixable && (
              <span className="text-xs bg-emerald-900/30 text-emerald-400 border border-emerald-700/50 px-2 py-0.5 rounded-full">auto-fixable</span>
            )}
            {issue.fix_applied && (
              <span className="text-xs bg-zinc-900/30 text-zinc-400 border border-zinc-600 px-2 py-0.5 rounded-full">fixed</span>
            )}
          </div>
          <span className="text-sm font-medium">{issue.title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform mt-0.5 ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-border/40 pt-3 space-y-2">
          <p className="text-xs text-muted-foreground leading-relaxed">{issue.description}</p>
          {issue.suggestion && (
            <p className="text-xs flex items-start gap-1.5">
              <Wrench className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground/80">{issue.suggestion}</span>
            </p>
          )}
          {issue.fix_snippet && <FixSnippet snippet={issue.fix_snippet} />}
          {showFix && !issue.fix_applied && onFix && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => onFix(issue.issue_id)}>
                <CheckCircle className="w-3 h-3 mr-1" /> Mark Fixed
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Category section ──────────────────────────────────────────────────────────
const CategorySection = ({ category, issues, onFix, showFix }) => {
  const [open, setOpen] = useState(true);
  const cfg = CAT[category] || { icon: Bug, label: category, color: 'text-muted-foreground' };
  const Icon = cfg.icon;
  const open_issues = issues.filter(i => !i.fix_applied);
  const worst = SEV_ORDER.find(s => open_issues.some(i => i.severity === s)) || 'info';
  const s = SEV[worst];

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className={`w-8 h-8 rounded-lg bg-black/30 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${cfg.color}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{cfg.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${s.badge}`}>
              {open_issues.length} issue{open_issues.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-border/50">
          <div className="pt-3 space-y-2">
            {issues.map(issue => (
              <IssueCard key={issue.issue_id} issue={issue} onFix={onFix} showFix={showFix} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const WebsiteHealth = () => {
  const { currentProjectId } = useProject();
  const { api } = useAuth();

  const [health, setHealth] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanUrl, setScanUrl] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterSev, setFilterSev] = useState('all');

  const fetchData = useCallback(async () => {
    if (!currentProjectId) return;
    setLoading(true);
    try {
      const [healthData, scansData] = await Promise.all([
        api(`/api/website/health?project_id=${currentProjectId}`),
        api(`/api/website/scans?project_id=${currentProjectId}`),
      ]);
      setHealth(healthData);
      setScans(scansData);
    } catch (err) {
      console.error('WebsiteHealth fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentProjectId, api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const triggerScan = async (url) => {
    const target = (url || scanUrl).trim();
    if (!target) { toast.error('Enter a URL to scan'); return; }
    const fullUrl = target.startsWith('http') ? target : `https://${target}`;

    setScanning(true);
    setScanResult(null);
    setActiveTab('overview');
    toast.info(`Scanning ${fullUrl}...`);
    try {
      const result = await api('/api/website/scan', {
        method: 'POST',
        body: JSON.stringify({ project_id: currentProjectId, url: fullUrl, scan_type: 'full' }),
      });
      setScanResult(result);
      setActiveTab('issues');
      const issueCount = result.issues?.length || 0;
      const score = result.health_score;
      toast.success(`Scan done — ${score}/100 health score, ${issueCount} issues found`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const markFixed = async (issueId) => {
    try {
      await api(`/api/website/issues/${issueId}/fix`, { method: 'POST' });
      toast.success('Marked as fixed');
      fetchData();
    } catch {
      toast.error('Failed to update');
    }
  };

  const displayScore = scanResult?.health_score ?? health?.health_score ?? null;
  const displayIssues = scanResult?.issues ?? [];
  const summary = scanResult?.summary ?? {};

  // Group and sort issues
  const issuesByCategory = {};
  const filteredIssues = filterSev === 'all' ? displayIssues : displayIssues.filter(i => i.severity === filterSev);
  filteredIssues.forEach(i => {
    const cat = i.category || 'other';
    if (!issuesByCategory[cat]) issuesByCategory[cat] = [];
    issuesByCategory[cat].push(i);
  });

  const orderedCategories = [
    ...CAT_ORDER.filter(c => issuesByCategory[c]),
    ...Object.keys(issuesByCategory).filter(c => !CAT_ORDER.includes(c)),
  ];

  const sevCounts = displayIssues.reduce((acc, i) => { acc[i.severity] = (acc[i.severity] || 0) + 1; return acc; }, {});

  const platforms = [
    { label: '🇰🇪 KaziLink.co.ke', url: 'https://kazilink.co.ke', tag: 'Kenya marketplace' },
    { label: '🇦🇺 SiteDeskApp.com', url: 'https://sitedeskapp.com', tag: 'AU field service' },
  ];

  return (
    <div data-testid="website-health-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Website Review Agent</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Comprehensive audit: SEO, performance, accessibility, mobile, conversion, trust, analytics, social
        </p>
      </div>

      {/* Scan controls */}
      <Card className="bg-card border-border card-glow">
        <CardContent className="p-5">
          <div className="flex gap-2 mb-3">
            <Input
              value={scanUrl}
              onChange={e => setScanUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && triggerScan()}
              placeholder="https://kazilink.co.ke"
              className="h-10"
              data-testid="scan-url"
            />
            <Button onClick={() => triggerScan()} disabled={scanning} className="h-10 px-5" data-testid="scan-btn">
              {scanning
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Scanning...</>
                : <><Play className="w-4 h-4 mr-2" />Scan</>}
            </Button>
          </div>

          {/* Quick launch */}
          <div className="flex flex-wrap gap-2">
            {platforms.map(p => (
              <button
                key={p.url}
                onClick={() => { setScanUrl(p.url); triggerScan(p.url); }}
                disabled={scanning}
                className="text-xs bg-black/30 hover:bg-black/50 disabled:opacity-50 border border-border rounded-lg px-3 py-2 flex flex-col items-start transition-colors"
              >
                <span className="font-medium">{p.label}</span>
                <span className="text-muted-foreground">{p.tag}</span>
              </button>
            ))}
          </div>

          {scanResult && !scanning && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground border-t border-border pt-3">
              <span className="font-medium text-foreground/80">{scanResult.url}</span>
              <span>·</span>
              <span>HTTP {scanResult.status_code}</span>
              <span>·</span>
              <span>{scanResult.response_time_ms?.toFixed(0)}ms</span>
              <span>·</span>
              <span>{scanResult.issues?.length || 0} issues</span>
              <button
                onClick={() => triggerScan(scanResult.url)}
                className="ml-auto flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                disabled={scanning}
              >
                <RefreshCw className="w-3 h-3" /> Rescan
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Score + checklist */}
      {scanResult && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Score ring */}
          <Card className="bg-card border-border card-glow flex items-center justify-center p-5">
            <div className="text-center">
              <ScoreRing score={displayScore} />
              <p className="text-xs text-muted-foreground mt-2">Health Score</p>
              {scanResult.issues?.length > 0 && (
                <p className="text-xs text-muted-foreground">{scanResult.issues.length} issues</p>
              )}
            </div>
          </Card>

          {/* Severity breakdown */}
          <Card className="bg-card border-border card-glow p-5 lg:col-span-3">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-semibold">Issue Breakdown</span>
            </div>

            {/* Severity bars */}
            <div className="space-y-2 mb-4">
              {SEV_ORDER.filter(s => sevCounts[s]).map(sev => {
                const count = sevCounts[sev] || 0;
                const maxCount = Math.max(...Object.values(sevCounts));
                const s = SEV[sev];
                return (
                  <div key={sev} className="flex items-center gap-3">
                    <span className={`text-xs w-14 capitalize font-medium ${s.text}`}>{sev}</span>
                    <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${s.dot} transition-all duration-500`}
                        style={{ width: `${(count / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-4 text-right">{count}</span>
                  </div>
                );
              })}
            </div>

            {/* Page checklist */}
            <div className="flex flex-wrap gap-1.5">
              <CheckTile label="HTTPS" ok={summary.is_https} />
              <CheckTile label="Mobile" ok={summary.mobile_friendly} />
              <CheckTile label="Title" ok={summary.page_title} />
              <CheckTile label="Meta desc" ok={summary.has_meta_description} />
              <CheckTile label="H1" ok={summary.h1_count === 1} warn={summary.h1_count > 1} />
              <CheckTile label="Canonical" ok={summary.has_canonical} />
              <CheckTile label="OG Tags" ok={summary.has_og_tags} />
              <CheckTile label="Twitter card" ok={summary.has_twitter_card} />
              <CheckTile label="Schema" ok={summary.has_schema} />
              <CheckTile label="Analytics" ok={summary.has_analytics} />
              <CheckTile label="Favicon" ok={summary.has_favicon} />
              <CheckTile label="Lang attr" ok={summary.has_lang} />
              <CheckTile label="robots.txt" ok={summary.robots_ok} />
              <CheckTile label="sitemap.xml" ok={summary.sitemap_ok} />
              <CheckTile label="Nav" ok={summary.has_nav} />
              <CheckTile label="Footer" ok={summary.has_footer} />
              {summary.social_links?.length > 0 && (
                <CheckTile label={`Social (${summary.social_links.length})`} ok={true} />
              )}
            </div>
          </Card>
        </div>
      )}

      {/* No scan yet — show history or prompt */}
      {!scanResult && !scanning && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-card border-border card-glow flex items-center justify-center p-8">
            <div className="text-center">
              <ScoreRing score={health?.health_score} />
              <p className="text-xs text-muted-foreground mt-2">
                {health?.health_score != null ? 'Last recorded score' : 'No scans yet'}
              </p>
            </div>
          </Card>
          <Card className="bg-card border-border card-glow p-5 lg:col-span-2">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Bug className="w-4 h-4 text-muted-foreground" />
              Summary from last scan
            </div>
            {health?.open_issues > 0 ? (
              <div className="space-y-2">
                {SEV_ORDER.filter(s => health?.severity_breakdown?.[s]).map(sev => (
                  <div key={sev} className="flex items-center gap-3">
                    <span className={`text-xs w-14 capitalize font-medium ${SEV[sev].text}`}>{sev}</span>
                    <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${SEV[sev].dot}`}
                        style={{ width: `${(health.severity_breakdown[sev] / health.open_issues) * 100}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground">{health.severity_breakdown[sev]}</span>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">Run a new scan to get the latest report.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Globe className="w-10 h-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Enter a URL above and click Scan to start</p>
                <p className="text-xs text-muted-foreground mt-1">Checks 20+ factors across SEO, performance, accessibility, and conversion</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Scanning animation */}
      {scanning && (
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
            <p className="font-semibold">Scanning website...</p>
            <p className="text-sm text-muted-foreground mt-1">
              Checking availability, SEO, performance, accessibility, analytics, social, and more
            </p>
          </CardContent>
        </Card>
      )}

      {/* Issue list — by category */}
      {scanResult && displayIssues.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Bug className="w-5 h-5 text-muted-foreground" />
              Issues Found
              <span className="text-muted-foreground font-normal text-base">({filteredIssues.length})</span>
            </h2>

            {/* Severity filter */}
            <div className="flex gap-1.5">
              {['all', ...SEV_ORDER.filter(s => sevCounts[s])].map(sev => (
                <button
                  key={sev}
                  onClick={() => setFilterSev(sev)}
                  className={`text-xs px-3 py-1 rounded-full border transition-all ${
                    filterSev === sev
                      ? sev === 'all' ? 'bg-primary/20 border-primary text-primary' : `${SEV[sev].bg} ${SEV[sev].border} ${SEV[sev].text}`
                      : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {sev === 'all' ? `All (${displayIssues.length})` : `${sev} (${sevCounts[sev]})`}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {orderedCategories.map(cat => (
              <CategorySection
                key={cat}
                category={cat}
                issues={issuesByCategory[cat]}
                onFix={markFixed}
                showFix={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* No issues */}
      {scanResult && displayIssues.length === 0 && !scanning && (
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-lg font-semibold">No issues detected</p>
            <p className="text-sm text-muted-foreground mt-1">
              {scanResult.url} passed all checks with a score of {displayScore}/100
            </p>
          </CardContent>
        </Card>
      )}

      {/* Scan history */}
      {scans.length > 0 && (
        <Card className="bg-card border-border card-glow">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" /> Scan History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-0">
              {scans.slice(0, 8).map((scan, idx) => (
                <div
                  key={scan.scan_id || idx}
                  className="flex items-center justify-between py-3 border-b border-border/40 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate max-w-[280px]">{scan.url}</span>
                      {scan.status === 'error' && (
                        <span className="text-xs text-red-400 flex-shrink-0">error</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(scan.started_at).toLocaleString()} ·{' '}
                      {scan.response_time_ms ? `${scan.response_time_ms}ms` : '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    {scan.issues?.length != null && (
                      <span className="text-xs text-muted-foreground">{scan.issues.length} issues</span>
                    )}
                    <span className={`text-lg font-bold ${
                      (scan.health_score || 0) >= 80 ? 'text-emerald-400' :
                      (scan.health_score || 0) >= 60 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {scan.health_score ?? '—'}
                    </span>
                    <button
                      onClick={() => { setScanUrl(scan.url); triggerScan(scan.url); }}
                      disabled={scanning}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WebsiteHealth;
